import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, Check, Zap } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { SPORTS_CONFIG, ENABLED_SPORTS } from '../config/sports';

const SUBSCRIPTION_PLANS = {
  '2_sports_monthly': { name: '2 Sports', sports_limit: 2, price: 2.99, billing_cycle: 'monthly' },
  '2_sports_yearly': { name: '2 Sports', sports_limit: 2, price: 14.99, billing_cycle: 'yearly' },
  '4_sports_monthly': { name: '4 Sports', sports_limit: 4, price: 4.99, billing_cycle: 'monthly' },
  '4_sports_yearly': { name: '4 Sports', sports_limit: 4, price: 16.99, billing_cycle: 'yearly' },
  'all_sports_monthly': { name: 'All Sports', sports_limit: 6, price: 6.99, billing_cycle: 'monthly' },
  'all_sports_yearly': { name: 'All Sports', sports_limit: 6, price: 19.99, billing_cycle: 'yearly' },
};

export default function Paywall() {
  const navigate = useNavigate();
  const { subscriptionStatus, checkSubscription } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState('all_sports_monthly');
  const [selectedSports, setSelectedSports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    checkSubscription();
  }, []);

  useEffect(() => {
    const planKey = `all_sports_${billingCycle}`;
    setSelectedPlan(planKey);
  }, [billingCycle]);

  const handleToggleSport = (sport) => {
    const plan = SUBSCRIPTION_PLANS[selectedPlan];
    setSelectedSports(prev => {
      if (prev.includes(sport)) {
        return prev.filter(s => s !== sport);
      }
      if (prev.length >= plan.sports_limit) {
        toast.error(`This plan allows only ${plan.sports_limit} sports`);
        return prev;
      }
      return [...prev, sport];
    });
  };

  const handleSubscribe = async () => {
    const plan = SUBSCRIPTION_PLANS[selectedPlan];
    
    if (selectedSports.length === 0) {
      toast.error('Please select at least one sport');
      return;
    }

    if (selectedSports.length > plan.sports_limit) {
      toast.error(`Please select up to ${plan.sports_limit} sports`);
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post('/stripe/create-checkout-session', {
        plan_id: selectedPlan,
        selected_sports: selectedSports,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error('Failed to start checkout');
    } finally {
      setIsLoading(false);
    }
  };

  const plans = [
    { key: '2_sports', name: '2 Sports', limit: 2 },
    { key: '4_sports', name: '4 Sports', limit: 4 },
    { key: 'all_sports', name: 'All Sports', limit: 6 },
  ];

  const currentPlan = SUBSCRIPTION_PLANS[selectedPlan];

  return (
    <div className="min-h-screen bg-[#000] pb-8">
      {/* Header */}
      <div className="p-4 flex items-center">
        <button
          data-testid="back-btn"
          onClick={() => navigate('/athletes')}
          className="flex items-center gap-1 text-white"
        >
          <ChevronLeft className="w-6 h-6" />
          <span>Back</span>
        </button>
      </div>

      <div className="px-4">
        {/* Title */}
        <div className="text-center mb-8">
          <Zap className="w-12 h-12 text-[#00E5E5] mx-auto mb-2" />
          <h1 className="text-2xl font-bold text-white mb-2">Unlock Unlimited Tracking</h1>
          <p className="text-gray-400">
            {subscriptionStatus?.games_remaining === 0 
              ? 'Your free trial has ended' 
              : `${subscriptionStatus?.games_remaining || 4} free games remaining`}
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex bg-[#1A1A1A] rounded-xl p-1 mb-6">
          <button
            data-testid="monthly-toggle"
            onClick={() => setBillingCycle('monthly')}
            className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
              billingCycle === 'monthly' ? 'bg-[#00E5E5] text-black' : 'text-gray-400'
            }`}
          >
            Monthly
          </button>
          <button
            data-testid="yearly-toggle"
            onClick={() => setBillingCycle('yearly')}
            className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
              billingCycle === 'yearly' ? 'bg-[#00E5E5] text-black' : 'text-gray-400'
            }`}
          >
            Yearly <span className="text-xs">Save 40%</span>
          </button>
        </div>

        {/* Plans */}
        <div className="space-y-3 mb-6">
          {plans.map(plan => {
            const planKey = `${plan.key}_${billingCycle}`;
            const planData = SUBSCRIPTION_PLANS[planKey];
            const isSelected = selectedPlan === planKey;
            
            return (
              <button
                key={planKey}
                data-testid={`plan-${planKey}`}
                onClick={() => {
                  setSelectedPlan(planKey);
                  setSelectedSports([]);
                }}
                className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center justify-between ${
                  isSelected 
                    ? 'border-[#00E5E5] bg-[#00E5E5]/10' 
                    : 'border-transparent bg-[#1A1A1A]'
                }`}
              >
                <div className="text-left">
                  <h3 className="text-white font-semibold">{plan.name}</h3>
                  <p className="text-sm text-gray-400">
                    Track up to {plan.limit} sports
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-[#00E5E5]">${planData.price}</p>
                  <p className="text-xs text-gray-500">/{billingCycle === 'monthly' ? 'mo' : 'yr'}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Sport Selection */}
        <div className="mb-6">
          <p className="text-sm text-gray-400 mb-3 font-semibold">
            SELECT YOUR SPORTS ({selectedSports.length}/{currentPlan?.sports_limit || 6})
          </p>
          <div className="grid grid-cols-3 gap-2">
            {ENABLED_SPORTS.map(sport => {
              const config = SPORTS_CONFIG[sport];
              const isSelected = selectedSports.includes(sport);
              return (
                <button
                  key={sport}
                  data-testid={`paywall-sport-${sport}`}
                  onClick={() => handleToggleSport(sport)}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    isSelected 
                      ? 'border-[#00E5E5] bg-[#00E5E5]/10' 
                      : 'border-transparent bg-[#1A1A1A]'
                  }`}
                >
                  <span className="text-2xl block">{config?.emoji}</span>
                  <span className={`text-xs ${isSelected ? 'text-[#00E5E5]' : 'text-gray-400'}`}>
                    {config?.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Features */}
        <div className="bg-[#1A1A1A] rounded-2xl p-4 mb-6">
          <h3 className="text-white font-semibold mb-3">What you get:</h3>
          <ul className="space-y-2">
            {[
              'Unlimited game tracking',
              'All stats & achievements',
              'Game history & trends',
              'Export your data anytime',
            ].map((feature, i) => (
              <li key={i} className="flex items-center gap-2 text-gray-300">
                <Check className="w-4 h-4 text-[#00E5E5]" />
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {/* Subscribe Button */}
        <button
          data-testid="subscribe-btn"
          onClick={handleSubscribe}
          disabled={isLoading || selectedSports.length === 0}
          className="w-full bg-[#00E5E5] text-black font-bold py-4 rounded-xl flex items-center justify-center gap-2 hover:bg-[#5FFBFB] transition-colors disabled:opacity-50"
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <>Subscribe for ${currentPlan?.price}/{billingCycle === 'monthly' ? 'mo' : 'yr'}</>
          )}
        </button>

        <p className="text-center text-xs text-gray-500 mt-4">
          Cancel anytime. Secure payment via Stripe.
        </p>
      </div>
    </div>
  );
}
